
import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Mail, CheckCircle } from 'lucide-react';
import PasswordInput from '@/components/auth/PasswordInput';
import NauticLogo from '@/components/ui/NauticLogo';
import PasswordStrengthIndicator, { validatePassword } from '@/components/auth/PasswordStrengthIndicator';
import SimpleCaptcha from '@/components/auth/SimpleCaptcha';

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const { signUp, signIn, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs.",
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && !validatePassword(password)) {
      toast({
        title: "Erreur",
        description: "Le mot de passe ne respecte pas les critères de sécurité.",
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && !captchaValid) {
      toast({
        title: "Erreur",
        description: "Veuillez résoudre le captcha.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = isSignUp 
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        let errorMessage = "Une erreur s'est produite.";
        
        if (error.message.includes('Email not confirmed')) {
          errorMessage = "Veuillez confirmer votre email avant de vous connecter. Vérifiez votre boîte de réception.";
        } else if (error.message.includes('already registered')) {
          errorMessage = "Cet email est déjà enregistré. Essayez de vous connecter.";
        } else if (error.message.includes('Invalid login credentials')) {
          errorMessage = "Email ou mot de passe incorrect.";
        } else if (error.message.includes('signup')) {
          errorMessage = "Erreur lors de l'inscription. Vérifiez vos informations.";
        }
        
        toast({
          title: "Erreur",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        if (isSignUp) {
          setShowEmailConfirmation(true);
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        } else {
          toast({
            title: "Connexion réussie",
            description: "Bienvenue dans Nautic Coach !",
          });
          navigate('/dashboard');
        }
      }
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast({ title: "Erreur", description: "Veuillez entrer votre adresse email.", variant: "destructive" });
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast({ title: "Erreur", description: "Une erreur s'est produite lors de l'envoi.", variant: "destructive" });
      } else {
        toast({
          title: "Email envoyé",
          description: "Consultez votre boîte de réception. Vous pourrez changer votre mot de passe dans Mon Compte.",
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch {
      toast({ title: "Erreur", description: "Une erreur inattendue s'est produite.", variant: "destructive" });
    } finally {
      setResetLoading(false);
    }
  };

  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen bg-nautic flex items-center justify-center p-4">
        <Card className="w-full max-w-md border border-nautic-gold/30 shadow-2xl bg-white">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-nautic-gold/15 p-4">
                <Mail className="h-10 w-10 text-nautic-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-nautic">
              Vérifiez votre email
            </CardTitle>
            <CardDescription className="text-base mt-2 text-muted-foreground">
              Un email de confirmation a été envoyé. Cliquez sur le lien dans l'email pour activer votre compte.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-md bg-nautic-gold/10 border border-nautic-gold/20 text-sm text-nautic">
              <CheckCircle className="h-5 w-5 text-nautic-gold mt-0.5 shrink-0" />
              <span>Pensez à vérifier votre dossier spam si vous ne trouvez pas l'email.</span>
            </div>
            <Button
              className="w-full bg-nautic hover:bg-nautic-light text-white"
              onClick={() => {
                setShowEmailConfirmation(false);
                setIsSignUp(false);
              }}
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nautic flex items-center justify-center p-4">
      <Card className="w-full max-w-md border border-nautic-gold/30 shadow-2xl bg-white">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <NauticLogo withText textSize="lg" className="text-nautic-gold" />
          </div>
          <CardDescription className="text-muted-foreground">
            {isSignUp ? "Créez votre compte" : "Connectez-vous à votre compte"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Entrez votre email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <PasswordInput
                id="password"
                placeholder="Entrez votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {isSignUp && (
              <PasswordStrengthIndicator password={password} />
            )}

            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="Confirmez votre mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}

            {isSignUp && (
              <SimpleCaptcha onValidChange={setCaptchaValid} />
            )}

            <Button 
              type="submit" 
              className="w-full bg-nautic hover:bg-nautic-light text-white"
              disabled={loading}
            >
              {loading ? 'Chargement...' : (isSignUp ? "S'inscrire" : "Connexion")}
            </Button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-nautic hover:underline text-sm"
            >
              {isSignUp 
                ? "Déjà un compte ? Connectez-vous" 
                : "Pas de compte ? Inscrivez-vous"}
            </button>

            {!isSignUp && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(!showForgotPassword)}
                  className="text-muted-foreground hover:text-nautic hover:underline text-xs"
                >
                  Mot de passe oublié ?
                </button>

                {showForgotPassword && (
                  <form onSubmit={handleResetPassword} className="mt-3 space-y-3 p-3 border rounded-md bg-muted/30">
                    <p className="text-xs text-muted-foreground">
                      Entrez votre email pour recevoir un lien de réinitialisation. Vous pourrez ensuite changer votre mot de passe dans <strong>Mon Compte</strong>.
                    </p>
                    <Input
                      type="email"
                      placeholder="Votre adresse email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      className="w-full"
                      disabled={resetLoading}
                    >
                      {resetLoading ? 'Envoi...' : 'Envoyer'}
                    </Button>
                  </form>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
